// src/services/auth.ts
// COMPLETE FILE - Copy this entire file

import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type { RootState } from "../app/store";

const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_API_BASE || "http://localhost:4000/api",
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).auth.token;
    if (token) headers.set("authorization", `Bearer ${token}`);
    headers.set("content-type", "application/json");
    return headers;
  },
});

export const authApi = createApi({
  reducerPath: "authApi",
  baseQuery,
  tagTypes: ["Auth"],
  endpoints: (builder) => ({
    // ========================================================================
    // AUTHENTICATION ENDPOINTS
    // ========================================================================

    register: builder.mutation({
      query: (body: any) => ({ url: "/auth/register", method: "POST", body }),
    }),

    login: builder.mutation({
      query: (body: any) => ({ url: "/auth/login", method: "POST", body }),
    }),

    logout: builder.mutation({
      query: () => ({ url: "/auth/logout", method: "POST" }),
    }),

    heartbeat: builder.mutation({
      query: () => ({ url: "/auth/heartbeat", method: "POST" }),
    }),

    // ========================================================================
    // PREVIEW COURSE ENDPOINT (NEW)
    // ========================================================================

    previewCourse: builder.mutation<
      {
        success: boolean;
        message: string;
        data: {
          email: string;
          firstName: string;
          hasPreviewAccess: boolean;
        };
      },
      {
        firstName: string;
        email: string;
        whatsapp: string;
      }
    >({
      query: (body) => ({
        url: "/auth/preview-course",
        method: "POST",
        body,
      }),
    }),

    // ========================================================================
    // PASSWORD MANAGEMENT ENDPOINTS
    // ========================================================================

    forgotPassword: builder.mutation<
      {
        success: boolean;
        message: string;
        resetToken?: string;
        resetUrl?: string;
      },
      { email: string }
    >({
      query: (body) => ({
        url: "/auth/forgot-password",
        method: "POST",
        body,
      }),
    }),

    verifyResetToken: builder.mutation<
      { success: boolean; message: string },
      { email: string; token: string }
    >({
      query: (body) => ({
        url: "/auth/verify-reset-token",
        method: "POST",
        body,
      }),
    }),

    resetPassword: builder.mutation<
      { success: boolean; message: string },
      { email: string; token: string; newPassword: string }
    >({
      query: (body) => ({
        url: "/auth/reset-password",
        method: "POST",
        body,
      }),
    }),

    changePassword: builder.mutation<
      { success: boolean; message: string },
      { currentPassword: string; newPassword: string }
    >({
      query: (body) => ({
        url: "/auth/change-password",
        method: "POST",
        body,
      }),
    }),

    // ========================================================================
    // COURSE/STREAM ENDPOINTS
    // ========================================================================

    getCourses: builder.query({
      query: () => ({ url: "/stream/courses", method: "GET" }),
    }),

    getPlayUrl: builder.mutation({
      query: (body: any) => ({ url: "/stream/play", method: "POST", body }),
    }),
  }),
});

// ============================================================================
// EXPORT HOOKS
// ============================================================================

export const {
  // Auth hooks
  useRegisterMutation,
  useLoginMutation,
  useLogoutMutation,
  useHeartbeatMutation,

  // Preview course hook (NEW)
  usePreviewCourseMutation,

  // Password management hooks
  useForgotPasswordMutation,
  useVerifyResetTokenMutation,
  useResetPasswordMutation,
  useChangePasswordMutation,

  // Course/stream hooks
  useGetCoursesQuery,
  useGetPlayUrlMutation,
} = authApi;

export default authApi;
